import { Box, Button, TextField } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, TrashIcon } from '~/assets/icons';
import { BaseHeaderTitle } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import PriceComponentList from '../../../components/price-components/PriceComponentList/PriceComponentList';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { catchError, handleApiError, notify } from '~/utils/helper';
import { handleGetPriceComponent, handleGetPriceComponents } from '~/thunks/price-components/priceComponentsThunk';
import useDebounce from '~/hooks/useDebounce';
import { priceComponentActions } from '~/thunks/price-components/priceComponentsSlice';
import styles from './PriceComponent.module.scss';
import classNames from 'classnames/bind';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import priceComponentApi from '~/apis/estimate';
import { IPayloadDeletePriceComponent, IPriceComponent } from '~/utils/interface/estimate';
import EditPriceComponentDialog from '~/components/price-components/EditPriceComponent/EditPriceComponentDialog';

const cx = classNames.bind(styles);

const PriceComponent = () => {
  const [searchInput, setSearchInput] = useState<string>('');
  const { t } = useTranslation(['admin/price-component', 'admin/component']);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);

  const [editDialogMode, setEditDialogMode] = useState<string>('');
  const dispatch = useAppDispatch();
  const searchInputDebounce = useDebounce<string>(searchInput);
  const [editData, setEditData] = useState<IPriceComponent>();
  const { filter, needRefreshData, selectedIds, openAlertDelete, menuSelectedId } = useAppSelector(
    (state) => state.priceComponent
  );

  const handleSetOpen = async (open: boolean, _needRefreshData?: boolean) => {
    setOpenEditDialog(open);
  };

  const handleOpen = () => {
    setEditDialogMode(DIALOG_MODE_CREATE);
    setOpenEditDialog(true);
  };

  const handleOpenEdit = async (id: string) => {
    const response = await dispatch(handleGetPriceComponent(id)).unwrap();
    setEditData(response);
    setEditDialogMode(DIALOG_MODE_EDIT);
    setOpenEditDialog(true);
    try {
    } catch (e) {
      handleApiError(e);
    }
  };

  const openAlertDeleteModal = useCallback(() => {
    dispatch(priceComponentActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(priceComponentActions.changeMenuSelectId(undefined));
    dispatch(priceComponentActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const onDeleteSelectedPriceComponents = useCallback(async () => {
    let payloadDeletePriceComponents: IPayloadDeletePriceComponent =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await priceComponentApi.deletePriceComponent(payloadDeletePriceComponents);
      notify('success', t('deleteSuccessMessage'));
      dispatch(priceComponentActions.changeNeedRefreshData(true));
      dispatch(priceComponentActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, menuSelectedId, selectedIds, t]);

  useEffect(() => {
    if (dispatch && typeof searchInputDebounce !== 'undefined') {
      dispatch(
        priceComponentActions.setFilter({
          search: searchInputDebounce,
          searchFields: ['name'],
          page: 1,
        })
      );
    }
  }, [searchInputDebounce, dispatch]);

  useEffect(() => {
    dispatch(handleGetPriceComponents(filter))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, filter, needRefreshData]);

  return (
    <Box className='w-full h-full'>
      <Box className='mt-[20px] flex flex-col min-[1080px]:flex-row item-center'>
        <Box className='flex flex-col min-[1080px]:flex-row min-[1080px]:flex-1 min-[1080px]:justify-between'>
          <BaseHeaderTitle title={t('title')} />
          <Box className='flex flex-row mt-[10px] min-[1080px]:mt-0 gap-[10px]'>
            <Button
              className={cx(
                { 'visibility: visible': selectedIds.length > 0 },
                { 'visibility: hidden': selectedIds.length <= 0 }
              )}
              onClick={openAlertDeleteModal}
              variant='contained'
              color='error'
              disableElevation
              startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
            >
              {t('delete')}
            </Button>
            <TextField
              className='w-[204px]'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('searchPlaceholder')}
              InputProps={{ startAdornment: <SearchIcon className='w-[16px]' /> }}
            />
          </Box>
        </Box>

        <Box className='flex flex-row mt-[10px] min-[1080px]:flex-row min-[1080px]:ml-[10px] min-[1080px]:mt-0 justify-between'>
          <Button
            className='h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] ml-[10px]'
            variant='contained'
            disableElevation
            startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
            onClick={handleOpen}
          >
            {t('addNew')}
          </Button>
          <EditPriceComponentDialog
            open={openEditDialog}
            setOpen={handleSetOpen}
            mode={editDialogMode}
            data={editData}
          />
        </Box>
      </Box>
      <Box className='mt-[40px]'>
        <PriceComponentList onOpenEdit={handleOpenEdit} data={editData} />
      </Box>
      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedPriceComponents}
      />
    </Box>
  );
};

export default PriceComponent;
